/**
 * Tool Selector Event types
 *
 * Represents the audit trail for tool-selector decision-tree runs.
 * Every completed recommendation is persisted; tool opens update the same row.
 */

/** One Q&A step captured in the decision path */
export interface ToolSelectorResponse {
  questionId: string;
  questionText: string;
  selectedValue: string;
  selectedText: string;
}

/** Domain model (camelCase) */
export interface ToolSelectorEvent {
  id: string;
  workspaceId: string | null;
  userId: string;
  userName: string | null;
  createdAt: Date;

  /** Full question-answer chain */
  responses: ToolSelectorResponse[];

  /** Ordered list of question IDs answered */
  decisionPath: string[];

  /** IDs and names of recommended methods */
  recommendedMethodIds: string[];
  recommendedMethodNames: string[];
  confidence: 'high' | 'medium' | 'low' | null;
  reasoning: string | null;

  /** Set when the user clicks "Open tool" */
  openedToolSlug: string | null;
  openedToolName: string | null;
  openedAt: Date | null;

  /** Where the selector was embedded */
  source: 'tools-page' | 'lab';
}

/** DB row shape (snake_case) */
export interface ToolSelectorEventRow {
  id: string;
  workspace_id: string | null;
  user_id: string;
  user_name: string | null;
  created_at: string;
  responses: ToolSelectorResponse[];
  decision_path: string[];
  recommended_method_ids: string[];
  recommended_method_names: string[];
  confidence: string | null;
  reasoning: string | null;
  opened_tool_slug: string | null;
  opened_tool_name: string | null;
  opened_at: string | null;
  source: string;
}

export function eventRowToDomain(row: ToolSelectorEventRow): ToolSelectorEvent {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    userId: row.user_id,
    userName: row.user_name,
    createdAt: new Date(row.created_at),
    responses: row.responses ?? [],
    decisionPath: row.decision_path ?? [],
    recommendedMethodIds: row.recommended_method_ids ?? [],
    recommendedMethodNames: row.recommended_method_names ?? [],
    confidence: (row.confidence as ToolSelectorEvent['confidence']) ?? null,
    reasoning: row.reasoning,
    openedToolSlug: row.opened_tool_slug,
    openedToolName: row.opened_tool_name,
    openedAt: row.opened_at ? new Date(row.opened_at) : null,
    source: (row.source as ToolSelectorEvent['source']) ?? 'tools-page',
  };
}

/** Input payload for logging a new recommendation */
export interface LogRecommendationInput {
  responses: ToolSelectorResponse[];
  decisionPath: string[];
  recommendedMethodIds: string[];
  recommendedMethodNames: string[];
  confidence: 'high' | 'medium' | 'low' | null;
  reasoning: string | null;
  source: 'tools-page' | 'lab';
}
